<script lang="ts">
/**
 * @component FingerprintIdentifier
 *
 * Inner component that performs Fingerprint visitor identification.
 * Must be rendered as a child of `FingerprintProvider` so the SDK context
 * is available.
 *
 * Calls `useVisitorData({ immediate: true, extendedResult: true })` at the
 * top level to comply with Svelte 5's requirement that store subscriptions
 * are declared at the component's top scope.
 *
 * When identification succeeds, reactively sends the full visitor data to
 * Google Analytics as custom user properties. Gracefully handles ad-blocker
 * or network failures that prevent the Fingerprint agent script from loading.
 *
 * @see {@link FingerprintTracker} for the parent provider wrapper
 */
import { useVisitorData } from "@fingerprint/svelte";
import { browser } from "$app/env";
import { ga } from "$lib/analytics";

// extendedResult: true returns additional fields like incognito, IP, browser details, etc.
const { data, error } = useVisitorData({
  immediate: true,
  extendedResult: true,
} as any);

// Log errors (typically ad-blocker blocking the agent script) but don't crash
$effect(() => {
  if (browser && $error) {
    console.warn(
      "[FingerprintIdentifier] Fingerprint identification failed (likely blocked by ad-blocker):",
      $error.message,
    );
  }
});

// Send all available Fingerprint data to Google Analytics when available
$effect(() => {
  const d = $data as any;
  if (browser && d?.visitorId) {
    ga.setUserProperties({
      fp_visitor_id: d.visitorId,
      fp_request_id: d.requestId,
      fp_confidence: String(d.confidence?.score ?? ""),
      fp_visitor_found: String(d.visitorFound ?? ""),
      fp_incognito: String(d.incognito ?? ""),
      fp_ip: d.ip ?? "",
      fp_browser: d.browserName ?? "",
      fp_browser_version: d.browserVersion ?? "",
      fp_os: d.os ?? "",
      fp_os_version: d.osVersion ?? "",
      fp_device: d.device ?? "",
    });

    ga.event("fingerprint_identified", {
      fp_visitor_id: d.visitorId,
      fp_confidence: d.confidence?.score,
      fp_visitor_found: d.visitorFound,
      fp_incognito: d.incognito,
      fp_first_seen: d.firstSeenAt?.global ?? "",
      fp_last_seen: d.lastSeenAt?.global ?? "",
      fp_ip: d.ip ?? "",
      fp_city: d.ipLocation?.city?.name ?? "",
      fp_country: d.ipLocation?.country?.code ?? "",
      fp_browser: d.browserName ?? "",
      fp_browser_version: d.browserVersion ?? "",
      fp_os: d.os ?? "",
      fp_os_version: d.osVersion ?? "",
      fp_device: d.device ?? "",
    });
  }
});
</script>
